import React, { useState, useMemo } from 'react';
import {
  HelpCircle,
  ShieldCheck,
  UserCheck,
  Send,
  Building2,
  Coins,
  CreditCard,
  Scale,
  Search,
  ChevronDown,
  ChevronRight,
  Mail,
  Sparkles,
  Layers,
  Lock,
  User,
  Briefcase
} from 'lucide-react';
import { LandingHeader } from './LandingHeader';
import LandingFooter from './LandingFooter';

interface FAQItem {
  q: string;
  a: React.ReactNode;
  category: 'general' | 'privacy' | 'candidates' | 'invitations' | 'recruiters' | 'credits' | 'payments' | 'platform';
}

interface NavCategory {
  id: string;
  title: string;
  icon: React.ElementType;
}

const categories: NavCategory[] = [
  { id: 'all', title: 'All Questions', icon: HelpCircle },
  { id: 'general', title: '1. General Questions', icon: HelpCircle },
  { id: 'privacy', title: '2. Privacy & Confidentiality', icon: ShieldCheck },
  { id: 'candidates', title: '3. Candidate Accounts', icon: UserCheck },
  { id: 'invitations', title: '4. Invitations & Opportunities', icon: Send },
  { id: 'recruiters', title: '5. Recruiter Verification', icon: Building2 },
  { id: 'credits', title: '6. Subscriptions & Credits', icon: Coins },
  { id: 'payments', title: '7. Payments & Billing', icon: CreditCard },
  { id: 'platform', title: '8. Platform Policies', icon: Scale },
//   { id: 'future-faq', title: '9. Future FAQ Roadmap', icon: Layers },
];

const faqData: FAQItem[] = [
  // 1. General Questions
  {
    category: 'general',
    q: 'What is Nexgent?',
    a: 'Nexgent is a private leadership hiring platform that connects experienced professionals with verified recruiters and organizations through a privacy-focused and invitation-driven recruitment process.',
  },
  {
    category: 'general',
    q: 'How is Nexgent different from traditional job portals?',
    a: (
      <div>
        <p className="mb-2">Unlike traditional job boards that rely on public profiles and mass applications, Nexgent focuses on:</p>
        <ul className="list-disc pl-5 space-y-1 text-slate-400">
          <li>Privacy-first hiring architecture</li>
          <li>Strictly verified recruiters and employers</li>
          <li>Candidate-controlled visibility settings</li>
          <li>Invitation-based executive engagement</li>
          <li>Curated leadership and senior professional talent</li>
        </ul>
      </div>
    ),
  },
  {
    category: 'general',
    q: 'Who can use Nexgent?',
    a: 'Nexgent is designed for experienced professionals, leadership talent, verified corporate recruiters, employers, and talent acquisition teams. Users must be at least 18 years of age.',
  },
  {
    category: 'general',
    q: 'Is Nexgent free for candidates?',
    a: 'Candidate access and platform features may vary over time. Any applicable fees or premium features will be clearly communicated within the platform.',
  },

  // 2. Privacy & Confidentiality
  {
    category: 'privacy',
    q: 'Is my employer notified when I join Nexgent?',
    a: 'No. Nexgent does not notify your current employer when you create an account, update your profile, or engage with opportunities on the platform.',
  },
  {
    category: 'privacy',
    q: 'Can my employer see that I am using Nexgent?',
    a: 'Not automatically. Only authorized and verified recruiters who have access through the platform can view information made available through Nexgent according to your visibility settings and platform policies.',
  },
  {
    category: 'privacy',
    q: 'Can I hide my profile?',
    a: 'Yes. Nexgent provides profile visibility controls that allow candidates to manage how their information is shared with recruiters. Available options may vary depending on platform settings.',
  },
  {
    category: 'privacy',
    q: 'Are my personal contact details publicly visible?',
    a: 'No. Personal information such as email addresses and phone numbers is not publicly displayed and is shared strictly according to platform privacy controls and recruiter access policies.',
  },
  {
    category: 'privacy',
    q: 'Is my data secure?',
    a: 'Nexgent implements security measures designed to help protect user information, including secure HTTPS communication protocols, authentication controls, and monitored platform access. For details, please review our Security Policy.',
  },

  // 3. Candidate Accounts
  {
    category: 'candidates',
    q: 'How do I create an account?',
    a: 'You can register by providing your professional information, verifying your email/phone, and completing any required profile validation steps.',
  },
  {
    category: 'candidates',
    q: 'Is profile verification required?',
    a: 'Verification may be required to help maintain profile quality and platform integrity. Verification requirements vary depending on the details provided and platform policies.',
  },
  {
    category: 'candidates',
    q: 'Why was my profile not approved?',
    a: 'Profiles may be declined or require additional review due to incomplete information, inaccurate details, duplicate accounts, policy violations, or failure to meet quality standards.',
  },
  {
    category: 'candidates',
    q: 'Can I update my profile after registration?',
    a: 'Yes. Candidates are encouraged to keep their professional experience, headline, and skill sets current and accurate.',
  },
  {
    category: 'candidates',
    q: 'Can I have multiple accounts?',
    a: 'Generally, no. To maintain platform quality and prevent duplicate profiles, candidates are typically limited to one active account unless expressly approved by Nexgent.',
  },
  {
    category: 'candidates',
    q: 'Can I pause my account?',
    a: 'Where available, users may temporarily deactivate profile visibility or limit participation without permanently deleting their account.',
  },
  {
    category: 'candidates',
    q: 'Can I delete my account?',
    a: 'Yes. Users may request account deletion through account settings or by contacting support. Certain information may be retained where required by law or for security purposes.',
  },

  // 4. Invitations & Opportunities
  {
    category: 'invitations',
    q: 'How do recruiters contact me?',
    a: 'Recruiters can send structured invitations through the platform when they identify an anonymized or public candidate profile that matches their hiring requirements.',
  },
  {
    category: 'invitations',
    q: 'What happens when I receive an invitation?',
    a: 'You may review the opportunity context and decide whether to Accept, Decline, or Ignore the invitation. You remain in complete control of your career participation.',
  },
  {
    category: 'invitations',
    q: 'What happens if I reject an invitation?',
    a: 'Rejecting an invitation simply indicates that you are not interested in that specific opportunity. There is no penalty or negative impact on your account visibility.',
  },
  {
    category: 'invitations',
    q: 'Can I change my mind after accepting an invitation?',
    a: 'Depending on the communication stage and platform functionality, you may choose not to proceed. Professional courtesy and clear communication with recruiters are encouraged.',
  },
  {
    category: 'invitations',
    q: 'Does accepting an invitation guarantee an interview or job offer?',
    a: 'No. Accepting an invitation indicates mutual interest but does not guarantee interviews, offers, employment, or specific hiring outcomes.',
  },

  // 5. Recruiter Verification
  {
    category: 'recruiters',
    q: 'Are recruiters verified?',
    a: 'Yes. Recruiters must complete verification workflows before accessing candidate search and executive outreach features to maintain a trusted environment.',
  },
  {
    category: 'recruiters',
    q: 'What does recruiter verification involve?',
    a: 'Verification includes company domain validation, business email checks, organizational legitimacy assessments, and identity checks.',
  },
  {
    category: 'recruiters',
    q: 'Can anyone view candidate profiles?',
    a: 'No. Access to candidate information is restricted according to platform policies, recruiter verification status, subscription tier permissions, and candidate privacy settings.',
  },

  // 6. Recruiter Subscriptions & Credits
  {
    category: 'credits',
    q: 'What are credits?',
    a: 'Credits are internal platform units that allow recruiters to perform specific actions, such as sending invitations or unlocking candidate contact details.',
  },
  {
    category: 'credits',
    q: 'When are credits deducted?',
    a: 'Credits are deducted strictly when a credit-based action is successfully completed. Applicable costs are clearly displayed prior to action execution.',
  },
  {
    category: 'credits',
    q: 'Do credits expire?',
    a: 'Credit validity depends on your active subscription plan (Ignite, Accelerate, Dominate) and platform terms. Please review the Credit Usage Policy for full details.',
  },
  {
    category: 'credits',
    q: 'Can unused credits be refunded?',
    a: 'Unused credits are generally non-refundable unless required by law or approved by Nexgent under exceptional circumstances.',
  },
  {
    category: 'credits',
    q: 'What happens if credits are deducted incorrectly?',
    a: 'If you believe credits were deducted due to a technical error, contact billing support. Nexgent will investigate and apply appropriate corrections where justified.',
  },

  // 7. Payments & Billing
  {
    category: 'payments',
    q: 'What payment methods are accepted?',
    a: 'Payment options are displayed during checkout through authorized and encrypted payment gateway partners.',
  },
  {
    category: 'payments',
    q: 'What should I do if payment succeeds but my subscription is not activated?',
    a: 'Contact billing@nexgent.tech with your transaction reference. Our team will investigate and either manually activate benefits or provide a prompt resolution.',
  },
  {
    category: 'payments',
    q: 'What happens if I accidentally make a duplicate payment?',
    a: 'Duplicate transactions are reviewed individually. Verified duplicate payments qualify for a refund or account credit adjustment.',
  },

  // 8. Platform Policies
  {
    category: 'platform',
    q: 'Does Nexgent guarantee jobs, interviews, or hires?',
    a: 'No. Nexgent provides a platform for professional connections. All employment and hiring decisions remain entirely between candidates and recruiters.',
  },
  {
    category: 'platform',
    q: 'Can Nexgent suspend an account?',
    a: 'Yes. Accounts may be restricted, suspended, or terminated for fraudulent activity, false disclosures, policy violations, security risks, or platform abuse.',
  },
  {
    category: 'platform',
    q: 'How can I report suspicious activity?',
    a: 'You can report suspicious behavior or security concerns directly to security@nexgent.tech.',
  },
];

/* ========================================================================
  FUTURE FAQ ROADMAP DATA STRUCTURE
  ========================================================================
  Uncomment/Use these items when ready to populate the roadmap section dynamically.

  const futureFaqCandidateItems = [
    { q: 'Can I make my profile invisible to specific companies?', a: 'Granular employer-blocking rules to hide your presence from past or present employers.' },
    { q: 'Can I control who views my profile?', a: 'Custom visibility tiers (Anonymized, Stealth, Full Executive Disclosure).' },
    { q: 'Will recruiters see my current employer?', a: 'Option to blur or mask current employer names until invitation acceptance.' },
    { q: 'Can I upload multiple resumes?', a: 'Support for multiple tailored CV versions mapped to different leadership domains.' },
    { q: 'How do I update my experience?', a: 'Seamless live profile editing with instant verification re-audits.' },
  ];

  const futureFaqRecruiterItems = [
    { q: 'How many credits does an invitation cost?', a: 'Dynamic credit pricing based on target candidate seniority levels.' },
    { q: 'Can credits be shared across team members?', a: 'Shared organization credit pools for enterprise recruitment teams.' },
    { q: 'Can I purchase additional credits?', a: 'On-demand credit top-up packages outside monthly plan renews.' },
    { q: 'Can I upgrade my subscription plan?', a: 'Prorated instant upgrades between Ignite, Accelerate, and Dominate tiers.' },
    { q: 'Can I downgrade my subscription?', a: 'Self-service tier downgrade options scheduled for the next billing cycle.' },
  ];

  const futureFaqPlatformItems = [
    { q: 'How does Nexgent verify candidate profiles?', a: 'AI-assisted automated checks paired with human verification review queues.' },
    { q: 'How does Nexgent prevent fake profiles?', a: 'Cross-platform similarity matching and identity verification barriers.' },
    { q: 'How does Nexgent protect my data?', a: 'Field-level database encryption and strict role-based access protocols.' },
    { q: 'How does Nexgent handle duplicate accounts?', a: 'Automated deduplication based on email, phone, and career signature matching.' },
    { q: 'How often are profiles reviewed?', a: 'Periodic periodic security audits and automated quarterly validity updates.' },
  ];
*/

export const FAQPolicy: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const toggleAccordion = (id: string) => {
    setOpenItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredFaqs = useMemo(() => {
    return faqData.filter((item) => {
      const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
      const matchesSearch =
        searchQuery === '' ||
        item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (typeof item.a === 'string' && item.a.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const scrollToSection = (id: string) => {
    setActiveCategory(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-cyan-500 selection:text-slate-950 relative overflow-hidden">
      <LandingHeader/>
      {/* Background Cyber Glow Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-cyan-500/10 blur-[140px] pointer-events-none rounded-full" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-blue-600/10 blur-[150px] pointer-events-none rounded-full" />

      {/* Header Banner */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-xs font-mono font-medium bg-cyan-950 text-cyan-400 border border-cyan-800/50 rounded-full tracking-wide">
                HELP CENTER & FAQ
              </span>
              <span className="text-xs text-slate-500">Motherson Infotech Division</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
              Frequently Asked Questions
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-slate-400 bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-800">
            <HelpCircle className="w-4 h-4 text-cyan-400" />
            <span>24/7 Platform Intelligence</span>
          </div>
        </div>
      </header>

      {/* Main Container Layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Navigation Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-24 bg-slate-900/70 border border-slate-800 rounded-xl p-4 backdrop-blur-md shadow-xl space-y-4">
            
            {/* Search Input */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Search FAQ..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 transition-all"
              />
            </div>

            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2">
              FAQ Categories
            </h2>

            <nav className="space-y-1">
              {categories.map((item) => {
                const Icon = item.icon;
                const isActive = activeCategory === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium rounded-lg transition-all text-left ${
                      isActive
                        ? 'bg-cyan-950/60 text-cyan-300 border border-cyan-800/60 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                      <span className="truncate">{item.title}</span>
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isActive ? 'text-cyan-400 translate-x-0.5' : 'text-slate-600'}`} />
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content Area */}
        <div className="lg:col-span-8 xl:col-span-9 space-y-10">
          
          {/* Active Accordion Section */}
          <section id="faq-list" className="space-y-4">
            
            {filteredFaqs.length === 0 && activeCategory !== 'future-faq' ? (
              <div className="p-8 bg-slate-900/40 border border-slate-800 rounded-2xl text-center space-y-2">
                <HelpCircle className="w-8 h-8 text-slate-600 mx-auto" />
                <p className="text-slate-400 text-sm">No matching questions found.</p>
                <p className="text-slate-500 text-xs">Try adjusting your search term or category filter.</p>
              </div>
            ) : (
              filteredFaqs.map((item, idx) => {
                const key = `faq-${idx}`;
                const isOpen = !!openItems[key];
                return (
                  <div
                    key={key}
                    className="bg-slate-900/40 border border-slate-800/80 rounded-2xl overflow-hidden transition-all hover:border-slate-700"
                  >
                    <button
                      onClick={() => toggleAccordion(key)}
                      className="w-full p-5 text-left flex items-center justify-between gap-4 font-semibold text-sm text-white hover:text-cyan-400 transition-colors"
                    >
                      <span className="flex items-center gap-3">
                        <span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0" />
                        {item.q}
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 text-slate-500 shrink-0 transition-transform duration-200 ${
                          isOpen ? 'rotate-180 text-cyan-400' : ''
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5 pt-1 text-slate-300 text-xs leading-relaxed border-t border-slate-800/50 bg-slate-950/40">
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })
            )}

          </section>

          {/* Section 9: Additional Sections / Future Roadmap */}
          {/* <section id="future-faq" className="scroll-mt-28 space-y-6 bg-slate-900/80 p-6 md:p-8 rounded-2xl border border-cyan-800/50 shadow-[0_0_30px_rgba(6,182,212,0.1)]">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-cyan-950 text-cyan-400 rounded-lg border border-cyan-800">
                <Layers className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">9. Future FAQ Roadmap</h2>
                <p className="text-xs text-slate-400">Expanded questions reserved for platform scaling</p>
              </div>
            </div> */}

            {/* ========================================================================
              COMMENTED OUT SECTION BELOW
              Uncomment the blocks below when you are ready to display these live!
              ========================================================================
            */}

            {/* <div className="space-y-6 pt-2">
              
              Candidate Future FAQs
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs font-mono text-cyan-400 font-semibold uppercase">
                  <User className="w-4 h-4" />
                  <span>For Candidates (Planned)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {futureFaqCandidateItems.map((item, idx) => (
                    <div key={idx} className="p-3.5 bg-slate-950 border border-slate-800 rounded-xl space-y-1">
                      <div className="text-white font-medium text-xs">{item.q}</div>
                      <p className="text-[11px] text-slate-400 leading-relaxed">{item.a}</p>
                    </div>
                  ))}
                </div>
              </div>

              Recruiter Future FAQs
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs font-mono text-cyan-400 font-semibold uppercase">
                  <Briefcase className="w-4 h-4" />
                  <span>For Recruiters (Planned)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {futureFaqRecruiterItems.map((item, idx) => (
                    <div key={idx} className="p-3.5 bg-slate-950 border border-slate-800 rounded-xl space-y-1">
                      <div className="text-white font-medium text-xs">{item.q}</div>
                      <p className="text-[11px] text-slate-400 leading-relaxed">{item.a}</p>
                    </div>
                  ))}
                </div>
              </div>

              Platform & Trust Future FAQs
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs font-mono text-cyan-400 font-semibold uppercase">
                  <Lock className="w-4 h-4" />
                  <span>Platform & Trust (Planned)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {futureFaqPlatformItems.map((item, idx) => (
                    <div key={idx} className="p-3.5 bg-slate-950 border border-slate-800 rounded-xl space-y-1">
                      <div className="text-white font-medium text-xs">{item.q}</div>
                      <p className="text-[11px] text-slate-400 leading-relaxed">{item.a}</p>
                    </div>
                  ))}
                </div>
              </div>

            </div>
            */}

            {/* Placeholder state while roadmap is commented out */}
            {/* <div className="p-4 bg-slate-950/60 border border-slate-800/80 rounded-xl text-center space-y-2">
              <div className="inline-flex p-2 bg-slate-900 text-cyan-400 rounded-lg border border-slate-800">
                <Sparkles className="w-4 h-4" />
              </div>
              <p className="text-xs text-slate-400 leading-relaxed max-w-lg mx-auto">
                Additional FAQ modules covering domain-level employer blocking, team credit sharing, multi-CV uploads, and dynamic AI profile auditing are preserved in code and ready to activate as platform usage grows.
              </p>
            </div> */}

            {/* Support Channels Banner */}
            {/* <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-2 text-xs">
              <div className="text-white font-semibold flex items-center gap-2">
                <Mail className="w-4 h-4 text-cyan-400" />
                <span>Still have unanswered questions?</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] font-mono text-slate-400 pt-1">
                <div>General Support: <span className="text-cyan-400">support@nexgent.tech</span></div>
                <div>Privacy Requests: <span className="text-cyan-400">privacy@nexgent.tech</span></div>
                <div>Billing Enquiries: <span className="text-cyan-400">billing@nexgent.tech</span></div>
                <div>Security Reports: <span className="text-cyan-400">security@nexgent.tech</span></div>
              </div>
            </div>

            <div className="p-3 bg-slate-950/40 rounded-lg border border-slate-800/80 text-center text-xs text-slate-500 font-mono mt-4">
              © {new Date().getFullYear()} Motherson Infotech / Nexgent FAQ & Knowledge Base. All rights reserved.
            </div>
          </section> */}

        </div>
      </main>
      <LandingFooter/>
    </div>
  );
};

export default FAQPolicy;